/**
 * Module (E15): Kraftwerk ist ein Alltag aus Bausteinen — wer nur trainiert,
 * sieht nur Training.
 *
 * Gespeichert im Profil (`profiles.modules`), also je Konto, über alle Geräte.
 * `null` heißt „alles an“: So behalten Konten von vor E15 genau, was sie
 * hatten, und ein Modul, das später dazukommt, ist für sie nicht still aus.
 *
 * Ein abgeschaltetes Modul verschwindet aus Reiterleiste und „Heute“ **und**
 * schweigt: Der Scheduler plant dafür keine Erinnerungen. Daten bleiben
 * erhalten — wieder einschalten bringt alles zurück.
 */

import { CHILD_NEVER_TABLES, CHILD_NUTRITION_TABLES, CHILD_TRAINING_TABLES } from './sync.js';

/**
 * Kosten sind **kein eigenes** Modul (Timo, 16.10.2026): Sie gehören zum
 * Haushalt — wer ihn nutzt, hat sie, wer ihn abschaltet, auch keine Kosten.
 * Ein altes `costs` in gespeicherten Profilen fällt still weg.
 */
export const MODULES = ['training', 'nutrition', 'planning', 'household', 'journal'] as const;
export type Module = (typeof MODULES)[number];

export function isModule(value: unknown): value is Module {
  return typeof value === 'string' && (MODULES as readonly string[]).includes(value);
}

/** Was gilt — `null` (nie gewählt) ist „alles“. Unbekanntes fällt weg. */
export function activeModules(stored: readonly string[] | null | undefined): Module[] {
  if (stored === null || stored === undefined) return [...MODULES];
  return MODULES.filter((module) => stored.includes(module));
}

export function moduleOn(stored: readonly string[] | null | undefined, module: Module): boolean {
  return activeModules(stored).includes(module);
}

export type TabKey = 'today' | 'training' | 'nutrition' | 'plan' | 'household' | 'journal';

/**
 * Reiter aus den Modulen. „Heute“ ist immer da; Kosten wohnen im Reiter
 * „Haushalt“. Mehr als sechs Reiter passen nicht in die Leiste.
 */
export function tabsFor(stored: readonly string[] | null | undefined): TabKey[] {
  const on = activeModules(stored);
  const tabs: TabKey[] = ['today'];
  if (on.includes('training')) tabs.push('training');
  if (on.includes('nutrition')) tabs.push('nutrition');
  if (on.includes('planning')) tabs.push('plan');
  if (on.includes('household')) tabs.push('household');
  if (on.includes('journal')) tabs.push('journal');
  return tabs;
}

/**
 * Was ein Kind immer nutzen kann (E16): Planen, Haushalt, Tagebuch. Keine
 * Kosten, und Zahlen über den eigenen Körper gehören nicht in die App eines
 * Kindes. Training und Ernährung gibt es nur für ein Teen mit Freigabe der
 * Eltern (`childMayUse`, unten).
 */
export const CHILD_MODULES: readonly Module[] = ['planning', 'household', 'journal'];

/**
 * Was ein Kind darf, über `CHILD_MODULES` hinaus — je Modul eine Freigabe der
 * Eltern, nur für die Stufe `teen`, ab Werk aus. Fällt die Stufe unter
 * `teen`, gilt keine Freigabe mehr — auch wenn ein Schalter noch stünde.
 *
 * - **Ernährung** (27.10.2026, Timo: „ja, darf ein Teen — er ist alt
 *   genug“, `household_members.teen_nutrition`).
 * - **Training** (28.10.2026, `household_members.teen_training`): Pläne,
 *   Übungen, Einheiten, Trainingstage, Erinnerungen, Fortschritt.
 *
 * Auch dann bleibt verborgen, was Körperzahlen braucht: Gewicht, Größe,
 * Grundumsatz und Zielvorschlag, Energie aus Training, Vergleichswerte für
 * Erwachsene, Supplements. Übungsgewichte (die Hantel im Satz) sind keine
 * Körperzahlen. Die Ziele setzt der Teen selbst. Erfolge (28.10.2026) nur für
 * Verhalten: `achievementScope` in `achievements.ts`.
 */
export const RELEASABLE_CHILD_MODULES = ['nutrition', 'training'] as const;
export type ReleasableChildModule = (typeof RELEASABLE_CHILD_MODULES)[number];

export interface ChildAccess {
  level?: unknown;
  /** Freigabe der Eltern für Ernährung (`teen_nutrition`). */
  nutrition?: boolean | null | undefined;
  /** Freigabe der Eltern für Training (`teen_training`). */
  training?: boolean | null | undefined;
}

function isReleasable(module: Module): module is ReleasableChildModule {
  return (RELEASABLE_CHILD_MODULES as readonly Module[]).includes(module);
}

/**
 * Darf dieses Kind das Modul nutzen? `CHILD_MODULES` immer, die
 * freischaltbaren nur als `teen` **und** freigegeben. Die eine Regel für
 * App, Server und Scheduler.
 */
export function childMayUse(access: ChildAccess | null | undefined, module: Module): boolean {
  if (CHILD_MODULES.includes(module)) return true;
  if (!isReleasable(module)) return false;
  return childLevelOf(access?.level) === 'teen' && access?.[module] === true;
}

/** Darf dieses Kind Ernährung nutzen? Nur `teen` **und** freigegeben. */
export function childMayUseNutrition(access: ChildAccess | null | undefined): boolean {
  return childMayUse(access, 'nutrition');
}

/** Darf dieses Kind trainieren? Nur `teen` **und** freigegeben (28.10.2026). */
export function childMayUseTraining(access: ChildAccess | null | undefined): boolean {
  return childMayUse(access, 'training');
}

/** Die Module, die ein Kind überhaupt einschalten kann. */
export function allowedChildModules(access?: ChildAccess | null): Module[] {
  return MODULES.filter((module) => childMayUse(access, module));
}

/**
 * Für ein Kind gilt die Wahl nur innerhalb von `allowedChildModules` — ohne
 * Angabe zur Freigabe also `CHILD_MODULES`.
 */
export function childModules(
  stored: readonly string[] | null | undefined,
  access?: ChildAccess | null,
): Module[] {
  const allowed = allowedChildModules(access);
  return activeModules(stored).filter((module) => allowed.includes(module));
}

/**
 * Profil-Module eines Kindes nach geänderter Freigabe oder Stufe — wie der
 * Server sie schreibt: neu Freigegebenes kommt dazu, Entzogenes fällt weg,
 * der Rest der Wahl bleibt (auch ein selbst abgeschaltetes, weiter
 * freigegebenes Modul bleibt aus). `null` (nie gewählt) gilt als
 * `CHILD_MODULES`.
 */
export function childModulesAfterRelease(
  stored: readonly string[] | null | undefined,
  before: ChildAccess | null | undefined,
  after: ChildAccess | null | undefined,
): Module[] {
  const kept = childModules(stored ?? [...CHILD_MODULES], after);
  return MODULES.filter(
    (module) =>
      kept.includes(module) || (childMayUse(after, module) && !childMayUse(before, module)),
  );
}

/**
 * Welche Tabelle ein Kind ohne passende Freigabe nicht schreibt — die eine
 * Stelle für den Push (`/sync/push`). `never`: Supplements, auch nicht als
 * Teen. `release`: das Modul ist nicht freigegeben. `null`: erlaubt (oder
 * erwachsen, `access` ist dann `null`).
 */
export function childWriteBlock(
  table: string,
  access: ChildAccess | null | undefined,
): 'never' | ReleasableChildModule | null {
  if (!access) return null;
  if ((CHILD_NEVER_TABLES as readonly string[]).includes(table)) return 'never';
  if ((CHILD_NUTRITION_TABLES as readonly string[]).includes(table)) {
    return childMayUseNutrition(access) ? null : 'nutrition';
  }
  if ((CHILD_TRAINING_TABLES as readonly string[]).includes(table)) {
    return childMayUseTraining(access) ? null : 'training';
  }
  return null;
}

/** Braucht diese Tabelle beim Kind eine Prüfung (`childWriteBlock`)? */
export function isChildGatedTable(table: string): boolean {
  return (
    (CHILD_NEVER_TABLES as readonly string[]).includes(table) ||
    (CHILD_NUTRITION_TABLES as readonly string[]).includes(table) ||
    (CHILD_TRAINING_TABLES as readonly string[]).includes(table)
  );
}

/**
 * Stufen eines Kindes (E20): Was es darf, legen die Eltern fest — das
 * Geburtsjahr schlägt nur vor. Eine Stufe, die am Geburtstag selbst
 * umspringt, würde Eltern überrumpeln.
 */
export const CHILD_LEVELS = ['small', 'school', 'teen'] as const;
export type ChildLevel = (typeof CHILD_LEVELS)[number];

export function isChildLevel(value: unknown): value is ChildLevel {
  return typeof value === 'string' && (CHILD_LEVELS as readonly string[]).includes(value);
}

/**
 * Gespeicherte Stufe lesen. Fehlt sie (Kinder von vor E20), gilt `school` —
 * das entspricht dem, was diese Kinder bis dahin durften.
 */
export function childLevelOf(stored: unknown): ChildLevel {
  return isChildLevel(stored) ? stored : 'school';
}

/** Vorschlag nach Alter: bis 9 klein, bis 13 Schulkind, danach jugendlich. */
export function suggestChildLevel(birthYear: number | null, currentYear: number): ChildLevel {
  if (birthYear === null) return 'school';
  const age = currentYear - birthYear;
  if (age <= 9) return 'small';
  if (age <= 13) return 'school';
  return 'teen';
}

/**
 * Was ein kleines Kind (`small`) nur liest: Termine, Aufgaben, Einkauf,
 * Bucketliste. Abhaken darf es nur eigene Aufgaben (`task_completions`, eigens
 * geprüft). Die Grenze zieht der Server beim Schreiben.
 */
export const SMALL_CHILD_READ_ONLY = [
  'events',
  'household_events',
  'task_lists',
  'tasks',
  'shops',
  'shopping_items',
  'pantry_items',
  'bucket_items',
  // Haushalts-Essensplan (27.10.2026): lesen ja, Wünsche erst ab `school`.
  'household_meals',
  'household_meal_eaters',
] as const;

export function smallChildMayWrite(table: string): boolean {
  return !(SMALL_CHILD_READ_ONLY as readonly string[]).includes(table);
}

/** Wer eine Notiz über ein Kind sehen darf (E16). */
export const NOTE_VISIBILITIES = ['me', 'adults', 'child'] as const;
export type NoteVisibility = (typeof NOTE_VISIBILITIES)[number];

export type MemberRole = 'adult' | 'child';
